import React from 'react';
import { MapPin, Trash2, Route, Clock } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных маршрутов
 */
const SavedRoutesSidebar = ({ 
  savedRoutes, 
  deleteSavedRoute, 
  loadSavedRoute 
}) => {
  // Если нет сохраненных маршрутов
  if (!savedRoutes || savedRoutes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-blue-500" />
          Сохраненные маршруты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные маршруты для сравнения.
        </p>
      </div>
    );
  }

  // Форматирование времени
  const formatTime = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    if (wholeHours === 0) return `${minutes} мин`;
    if (minutes === 0) return `${wholeHours} ч`;
    return `${wholeHours} ч ${minutes} мин`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-blue-500" />
          Сохраненные маршруты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedRoutes.length} {pluralize(savedRoutes.length, 'маршрут', 'маршрута', 'маршрутов')}
        </span>
      </div>

      <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
        {savedRoutes.map((route) => (
          <div 
            key={route.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {route.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<Route className="w-4 h-4" />}
                  onClick={() => loadSavedRoute(route.id)}
                  title="Загрузить маршрут"
                />
                <Button
                  size="xs"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedRoute(route.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Расстояние:</span>
                <span className="font-medium">
                  {formatNumber(route.results.distance, 0)} км
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Время в пути:</span>
                <span>
                  {formatTime(route.results.totalTime)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Скорость:</span>
                <span>
                  {formatNumber(route.results.effectiveSpeed, 0)} км/ч
                </span>
              </div>
              <div className="flex justify-between">
                <span>Дата создания:</span>
                <span className="text-xs">
                  {new Date(route.date).toLocaleDateString('ru-RU')}
                </span>
              </div>
            </div>
            
            {/* Дополнительная информация о маршруте */}
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-1">
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {new Date(route.results.departureTime).toLocaleDateString('ru-RU', {day: 'numeric', month: 'short'})}
              </div>
              <div>
                {route.formData.vehicleType === 'car' ? 'Автомобиль' : 
                 route.formData.vehicleType === 'motorcycle' ? 'Мотоцикл' : 
                 route.formData.vehicleType === 'bus' ? 'Автобус' : 
                 route.formData.vehicleType === 'truck' ? 'Грузовик' : 'Транспорт'}
              </div>
              <div>
                {route.formData.roadType === 'highway' ? 'Автомагистраль' : 
                 route.formData.roadType === 'federal' ? 'Федеральная' : 
                 route.formData.roadType === 'regional' ? 'Региональная' : 
                 route.formData.roadType === 'urban' ? 'Городская' : 'Дорога'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedRoutes.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <p>Сравните разные маршруты для оптимального планирования поездки.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default SavedRoutesSidebar;